import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Divider, Typography } from '@mui/material';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import NavNeededIndicator from '@sharedCR/common/NavNeededIndicator';
import {
  getNavNeededState,
  getTabAreaIdsBySectionHref,
} from '@sharedCR/common/NavNeededIndicator/NavNeededIndicator.utils';
import PageBlock from '@sharedCR/common/PageBlock';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PendingChangeWrapperCDS from '@sharedCR/coreDesignSystem/PendingChangeWrapperCDS';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';

import { FeatureFlag } from 'constants/featureFlags';
import { PendingChangesActionType } from 'constants/pendingChanges';
import usePopover from 'hooks/usePopover';
import { calculatePendingChanges } from 'utils/pendingChanges';

import {
  selectIncompleteLinkedNav,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsLoanLockedFromPropertyAndLoanSummary,
  selectIsProgramChangeRequired,
  selectIsUnsecuredFromPropertyAndLoanSummary,
} from 'store/loanApplications/selectors';

import ActionMenu from '../ActionMenu';
import {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
} from '../EditModal/EditModal.constants';

import Chart from './Chart';
import KeyDates from './KeyDates';
import { summaryBlocksWrapperStyles, titleWrapperStyles } from './LoanAndLockInfo.styles';
import MetricCards from './MetricCards';
import Summary from './Summary';

const BLOCK_TITLE = 'Loan & Lock Info';

const LoanAndLockInfo = ({ isLoading }) => {
  const { applicationId } = useParams();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isChartVisible = useFeatureToggle(FeatureFlag.EnableChartPropertyAndLoan);

  const isProgramChangeRequired = useSelector(selectIsProgramChangeRequired);
  const isLoanLocked = useSelector(selectIsLoanLockedFromPropertyAndLoanSummary);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const navNeededByAreaId = useSelector(selectIncompleteLinkedNav());

  const loanAndLockAreaIds = useMemo(
    () => Object.values(LOAN_AND_LOCK_SECTION_HREF_MAP).flatMap(getTabAreaIdsBySectionHref),
    [],
  );
  const { isNeeded: isSectionNeeded, count: neededCount } = getNavNeededState(
    navNeededByAreaId,
    loanAndLockAreaIds,
  );

  const isShownRepriceRequired = isProgramChangeRequired && isLoanLocked;
  const isChartHidden = !isChartVisible || isUnsecured || isChattel;

  const applicationEntityIds = useMemo(() => [applicationId], [applicationId]);

  const applicationCustomFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.LOAN,
    entityIds: applicationEntityIds,
  });

  const applicationCustomFieldsPendingChangesCount = _.sum(
    Object.values(calculatePendingChanges(applicationCustomFieldsPendingChanges, true)),
  );

  return (
    <PageBlock
      withAction
      withDivider
      isMenuOpened={!!anchorEl}
      title={
        <Box component="span" sx={titleWrapperStyles}>
          {BLOCK_TITLE}
          {isSectionNeeded && (
            <>
              <NavNeededIndicator />
              <NavNeededCountBadge count={neededCount} />
            </>
          )}
          {isShownRepriceRequired && (
            <LabelCDS size={LabelSize.S} title="Reprice required" variant={LabelVariant.RED} />
          )}
          {!!applicationCustomFieldsPendingChangesCount && (
            <PendingChangeWrapperCDS
              isSmall
              pendingChangesActionType={PendingChangesActionType.MODIFIED}
              textSelector="span"
            >
              <Typography component="span" variant="regular12">
                {applicationCustomFieldsPendingChangesCount}{' '}
                {applicationCustomFieldsPendingChangesCount > 1
                  ? 'Pending Changes'
                  : 'Pending Change'}
              </Typography>
            </PendingChangeWrapperCDS>
          )}
        </Box>
      }
      onMenuClick={onSetAnchorElement}
    >
      <MetricCards isLoading={isLoading} />
      <Box sx={summaryBlocksWrapperStyles}>
        <Summary isLoading={isLoading} />
        {!isUnsecured && (
          <>
            <Divider flexItem orientation="vertical" />
            <KeyDates isLoading={isLoading} />
          </>
        )}
      </Box>
      {!isChartHidden && (
        <>
          <Divider />
          <Chart isLoading={isLoading} />
        </>
      )}
      <ActionMenu
        anchorEl={anchorEl}
        editText="Edit Loan & Lock Info"
        entityType={ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK}
        onClearAnchorElement={onClearAnchorElement}
      />
    </PageBlock>
  );
};

LoanAndLockInfo.propTypes = {
  ...WithLoadingPropTypes,
};

export default LoanAndLockInfo;
